"use client";

import {
    listAdminMembersApiV1AdminMembersGet,
    updateMemberRoleApiV1AdminMembersPatch,
    removeMemberApiV1AdminMembersDelete,
} from "@/src/api/sdk.gen";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Users, Shield, ShieldCheck, UserMinus, RefreshCw, Building2, ChevronDown } from "lucide-react";

interface Member {
    id: string;
    membershipId: number;
    orgId: number;
    orgName: string;
    publicUser: {
        userId: string;
        identifier: string;
    } | null;
    role: "admin" | "member";
    createdAt: string;
}

export function MemberManager() {
    const [members, setMembers] = useState<Member[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedOrgId, setSelectedOrgId] = useState<string>("all");
    const [searchQuery, setSearchQuery] = useState("");

    const fetchMembers = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const { data, error: reqErr } = await listAdminMembersApiV1AdminMembersGet();
            if (!reqErr && data) {
                setMembers(data as unknown as Member[]);
            } else {
                const errObj = (reqErr || {}) as { error?: string };
                setError(errObj.error || "Failed to fetch members");
            }
        } catch (err) {
            console.error("Failed to fetch members:", err);
            setError("An error occurred while fetching members");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    // Unique orgs from loaded members
    const adminOrgs = useMemo(() => {
        const seen = new Map<number, string>();
        members.forEach((m) => seen.set(m.orgId, m.orgName));
        return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
    }, [members]);

    // Filtered members based on selected org + search query
    const filteredMembers = useMemo(() => {
        const byOrg =
            selectedOrgId === "all"
                ? members
                : members.filter((m) => String(m.orgId) === selectedOrgId);

        const query = searchQuery.trim().toLowerCase();
        if (!query) return byOrg;

        return byOrg.filter((m) => {
            const identifier = m.publicUser?.identifier?.toLowerCase() ?? "";
            const orgName = m.orgName.toLowerCase();
            const role = m.role.toLowerCase();
            return identifier.includes(query) || orgName.includes(query) || role.includes(query);
        });
    }, [members, selectedOrgId, searchQuery]);

    const handleUpdateRole = async (userId: string, orgId: number, newRole: string) => {
        setIsUpdating(`${orgId}::${userId}`);
        try {
            const { error: reqErr } = await updateMemberRoleApiV1AdminMembersPatch({
                body: { userId, role: newRole, orgId },
            });

            if (!reqErr) {
                fetchMembers();
            } else {
                const errObj = (reqErr || {}) as { error?: string };
                alert(errObj.error || "Failed to update role");
            }
        } catch (err) {
            alert("An error occurred while updating role");
        } finally {
            setIsUpdating(null);
        }
    };

    const handleRemoveMember = async (userId: string, orgId: number, orgName: string, userName: string) => {
        if (!confirm(`Are you sure you want to remove ${userName} from ${orgName}?`)) return;

        setIsUpdating(`${orgId}::${userId}`);
        try {
            const { error: reqErr } = await removeMemberApiV1AdminMembersDelete({
                body: { userId, orgId },
            });

            if (!reqErr) {
                fetchMembers();
            } else {
                const errObj = (reqErr || {}) as { error?: string };
                alert(errObj.error || "Failed to remove member");
            }
        } catch (err) {
            alert("An error occurred while removing member");
        } finally {
            setIsUpdating(null);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-lg font-bold text-slate-900 tracking-tight">Active Team Directory</h2>
                        <p className="text-sm text-slate-500 font-medium">Members across all organizations you admin</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by user, org, or role"
                            className="w-56 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        {/* Org filter */}
                        <div className="relative">
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <select
                                value={selectedOrgId}
                                onChange={(e) => setSelectedOrgId(e.target.value)}
                                className="pl-9 pr-8 py-2 text-sm font-normal rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-500 outline-none appearance-none text-slate-700 cursor-pointer"
                            >
                                <option value="all">All Organizations</option>
                                {adminOrgs.map((org) => (
                                    <option key={org.id} value={org.id}>{org.name}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                        </div>
                        <button
                            onClick={fetchMembers}
                            disabled={isLoading}
                            className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-white rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm disabled:opacity-50"
                        >
                            <RefreshCw className={`w-5 h-5 ${isLoading ? "animate-spin" : ""}`} />
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="p-6 m-6 bg-amber-50 border border-amber-100 rounded-xl shadow-sm">
                        <div className="flex items-center gap-3 text-amber-800 font-bold mb-3">
                            <Shield className="w-5 h-5 text-amber-600" />
                            Could not load members
                        </div>
                        <p className="text-sm text-amber-700 font-medium leading-relaxed">{error}</p>
                        <div className="mt-4">
                            <button
                                onClick={fetchMembers}
                                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm transition-all"
                            >
                                <RefreshCw className="w-3 h-3" /> Try Again
                            </button>
                        </div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-200">
                                <th className="px-6 py-4 text-xs font-extrabold text-slate-900 uppercase tracking-widest">User</th>
                                <th className="px-6 py-4 text-xs font-extrabold text-slate-900 uppercase tracking-widest">Role</th>
                                <th className="px-6 py-4 text-xs font-extrabold text-slate-900 uppercase tracking-widest">Joined Date</th>
                                <th className="px-6 py-4 text-xs font-extrabold text-slate-900 uppercase tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center text-slate-400 font-medium">
                                        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 opacity-20" />
                                        Loading members...
                                    </td>
                                </tr>
                            ) : filteredMembers.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center text-slate-400 font-medium italic">
                                        No members found
                                    </td>
                                </tr>
                            ) : (
                                filteredMembers.map((member) => {
                                    const updatingKey = `${member.orgId}::${member.publicUser?.userId}`;
                                    const isThisUpdating = isUpdating === updatingKey;
                                    return (
                                        <tr key={member.id} className="hover:bg-slate-50/50 transition-colors group">
                                            {/* User */}
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-10 w-10 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0">
                                                        <div className="h-full w-full flex items-center justify-center bg-slate-200 text-slate-400">
                                                            <Users className="w-5 h-5" />
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-bold text-slate-900">
                                                            {member.publicUser?.identifier || "Unknown User"}
                                                        </span>
                                                        <span className="text-xs text-slate-500 font-medium">{member.orgName}</span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Role */}
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    {member.role === "admin" ? (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                            <ShieldCheck className="w-3 h-3" /> Admin
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                                                            <Shield className="w-3 h-3" /> Member
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Joined */}
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-slate-600 font-medium whitespace-nowrap">
                                                    {new Date(member.createdAt).toLocaleDateString()}
                                                </span>
                                            </td>

                                            {/* Actions */}
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <select
                                                        value={member.role}
                                                        disabled={isThisUpdating || !member.publicUser}
                                                        onChange={(e) =>
                                                            member.publicUser &&
                                                            handleUpdateRole(member.publicUser.userId, member.orgId, e.target.value)
                                                        }
                                                        className="text-xs font-bold border border-slate-200 rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 transition-all bg-white"
                                                    >
                                                        <option value="member">Make Member</option>
                                                        <option value="admin">Make Admin</option>
                                                    </select>
                                                    <button
                                                        onClick={() =>
                                                            member.publicUser &&
                                                            handleRemoveMember(
                                                                member.publicUser.userId,
                                                                member.orgId,
                                                                member.orgName,
                                                                member.publicUser.identifier || "this user"
                                                            )
                                                        }
                                                        disabled={isThisUpdating || !member.publicUser}
                                                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100 disabled:opacity-50"
                                                        title={`Remove from ${member.orgName}`}
                                                    >
                                                        <UserMinus className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
